import { APPEARANCE_STORAGE_KEY, PREFERS_DARK_QUERY, type Appearance } from './appearance';
import { readAppearance } from './appearance-storage';

const DARK_CLASS = 'dark';

export function resolvedColorScheme(appearance: Appearance, systemDark: boolean): 'light' | 'dark' {
  if (appearance.colorMode === 'dark') {
    return 'dark';
  }
  if (appearance.colorMode === 'light') {
    return 'light';
  }
  return systemDark ? 'dark' : 'light';
}

/** Single DOM writer for class, data attributes, and color-scheme. */
export function applyAppearance(
  root: HTMLElement,
  appearance: Appearance,
  systemDark: boolean,
): void {
  const scheme = resolvedColorScheme(appearance, systemDark);
  root.classList.toggle(DARK_CLASS, scheme === 'dark');
  root.dataset.variant = appearance.designVariant;
  root.dataset.colorMode = appearance.colorMode;
  root.style.colorScheme = scheme;
  syncThemeColor(root);
}

/** Browser chrome around the app. Falls back to the static meta when tokens are not painted yet. */
function syncThemeColor(root: HTMLElement): void {
  const meta = root.ownerDocument?.querySelector('meta[name="theme-color"]');
  if (!meta) {
    return;
  }
  const color = getComputedStyle(root).getPropertyValue('--background').trim();
  if (!color) {
    return;
  }
  meta.setAttribute('content', color);
}

export function applyStoredAppearance(root: HTMLElement): Appearance {
  const appearance = readAppearance(browserStorage());
  applyAppearance(root, appearance, browserPrefersDark());
  return appearance;
}

/**
 * Re-applies when another tab changes the record.
 * The system-scheme listener exists only while the stored mode is `system`.
 */
export function watchStoredAppearance(root: HTMLElement, onApplied?: () => void): () => void {
  let media: MediaQueryList | null = null;

  const applyCurrent = () => {
    const appearance = applyStoredAppearance(root);
    syncMedia(appearance.colorMode === 'system');
    onApplied?.();
  };

  const syncMedia = (system: boolean) => {
    if (!system) {
      media?.removeEventListener('change', applyCurrent);
      media = null;
      return;
    }
    if (media) {
      return;
    }
    media = browserDarkMedia();
    media?.addEventListener('change', applyCurrent);
  };

  applyCurrent();

  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== APPEARANCE_STORAGE_KEY) {
      return;
    }
    applyCurrent();
  };

  window.addEventListener('storage', onStorage);
  return () => {
    media?.removeEventListener('change', applyCurrent);
    media = null;
    window.removeEventListener('storage', onStorage);
  };
}

function browserStorage(): Pick<Storage, 'getItem'> | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function browserPrefersDark(): boolean {
  try {
    return browserDarkMedia()?.matches === true;
  } catch {
    return false;
  }
}

function browserDarkMedia(): MediaQueryList | null {
  if (typeof window.matchMedia !== 'function') {
    return null;
  }
  return window.matchMedia(PREFERS_DARK_QUERY);
}
